import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api, { describeError } from "@/lib/api";
import { Kpi, Panel, Skeleton, ErrorState, Empty, Badge, day, dayTime, num, bytes, inr } from "@/components/platform/ui";

export default function PlatformOrgDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/platform/organizations/${id}`);
      setData(data);
    } catch (e) {
      setErr(describeError(e, "Could not load this organisation."));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const crumbs = (
    <nav data-testid="owner-breadcrumbs" className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <Link to="/platform" className="hover:text-navy">Platform</Link>
      <ChevronRight className="h-3 w-3" />
      <Link to="/platform/organizations" className="hover:text-navy" data-testid="crumb-organizations">
        Organizations
      </Link>
      <ChevronRight className="h-3 w-3" />
      <span className="text-navy">{data?.organization?.name || id}</span>
    </nav>
  );

  if (loading) return <div className="space-y-6">{crumbs}<Skeleton rows={6} testid="org-detail-loading" /></div>;
  if (err) return <div className="space-y-6">{crumbs}<ErrorState message={err} onRetry={load} testid="org-detail-error" /></div>;

  const { organization: org, subscription: sub, usage, members, recent_activity: activity, payments } = data;

  return (
    <div data-testid="owner-org-detail" className="space-y-6">
      {crumbs}
      <div>
        <h1 className="font-display text-3xl tracking-tight text-navy sm:text-4xl">{org.name}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {org.type === "law_firm" ? "Law firm" : "Individual advocate"} · created {day(org.created_at)} · {org.id}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi testid="detail-cases" label="Cases" value={num(usage.cases)} />
        <Kpi testid="detail-clients" label="Clients" value={num(usage.clients)} />
        <Kpi testid="detail-hearings" label="Hearings" value={num(usage.hearings)} />
        <Kpi testid="detail-tasks" label="Tasks" value={num(usage.tasks)} />
        <Kpi testid="detail-documents" label="Documents" value={num(usage.documents)} />
        <Kpi testid="detail-storage" label="Storage used" value={bytes(usage.storage_bytes)} />
        <Kpi testid="detail-members" label="Members" value={num(usage.users)} />
      </div>

      <Panel title="Subscription" testid="detail-subscription">
        <dl className="grid grid-cols-1 gap-x-8 gap-y-3 p-5 sm:grid-cols-2">
          {[
            ["Plan", sub.plan], ["Status", (sub.subscription_status || "—").replace("_", " ")],
            ["Billing cycle", sub.billing_cycle], ["Provider", sub.provider || "—"],
            ["Trial ends", day(sub.trial_ends_at)], ["Period ends", day(sub.current_period_end)],
            ["Started", day(sub.subscription_started_at)], ["Cancelled", day(sub.cancelled_at)],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 border-b border-border/70 pb-2">
              <dt className="text-xs text-muted-foreground">{k}</dt>
              <dd className="text-xs font-medium text-navy">{v || "—"}</dd>
            </div>
          ))}
        </dl>
        {!!payments?.length && (
          <div className="flex flex-wrap gap-3 border-t border-border px-5 py-4" data-testid="detail-payments">
            {payments.map((p) => (
              <span key={p.status} className="rounded-md bg-secondary px-3 py-2 text-xs text-navy">
                {p.status}: {p.count} · {inr(p.total)}
              </span>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Members" testid="detail-members-panel">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm" data-testid="detail-members-table">
            <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>{["Name", "Email", "Role", "Joined", "Last sign-in"].map((h) => (
                <th key={h} className="px-5 py-3 font-medium">{h}</th>))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {members.map((m) => (
                <tr key={m.id} data-testid={`member-row-${m.id}`}>
                  <td className="px-5 py-3 font-medium text-navy">{m.name}</td>
                  <td className="px-5 py-3 text-muted-foreground">{m.email}</td>
                  <td className="px-5 py-3"><Badge>{(m.role || "").replace("_", " ")}</Badge></td>
                  <td className="px-5 py-3 text-muted-foreground">{day(m.created_at)}</td>
                  <td className="px-5 py-3 text-muted-foreground">{dayTime(m.last_login_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel title="Recent activity" testid="detail-activity-panel">
        {!activity?.length ? <Empty message="No recorded activity yet." testid="detail-activity-empty" /> : (
          <ul className="divide-y divide-border">
            {activity.map((a, i) => (
              <li key={i} className="px-5 py-3" data-testid={`activity-row-${i}`}>
                <p className="text-sm font-medium text-navy">{a.event}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {a.detail} {a.actor ? `· ${a.actor}` : ""} · {dayTime(a.created_at)}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}
